import { Map, View } from 'ol';
import OpenLayersTileLayer from 'ol/layer/Tile';
import { defaults as defaultControls } from 'ol/control';
import { toPng } from 'html-to-image';
import { useEffect, useState } from 'react';
import { BaseLayerConfig } from './useBaseLayerConfigs';
import OSM from 'ol/source/OSM';
import {
  fetchTileWithRetry,
  S57ToTileLayer,
  wmsToTileLayer,
} from '@ais-ui/react-openlayers';
import { WMSLayerInfo } from '@ais-ui/websockets';
import { useWMSSettings } from '../../WMSLayers/useWMSSettings';
import { getS57Url } from '../../utils/getS57Url';

type Snapshots = {
  [key: string]: string;
};

// Helper function to compress image data
const compressImage = async (
  dataUrl: string,
  quality = 0.5
): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);
      // Convert to JPEG with lower quality for smaller size
      const compressed = canvas.toDataURL('image/jpeg', quality);
      resolve(compressed);
    };
    img.src = dataUrl;
  });
};

// Helper function to safely store in localStorage with error handling
const safeLocalStorageSet = async (
  key: string,
  value: string
): Promise<boolean> => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'QuotaExceededError') {
      // Try to clear old snapshots first
      clearOldSnapshots();
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (retryError) {
        console.warn(
          `Failed to store snapshot for ${key} even after cleanup:`,
          retryError
        );
        return false;
      }
    }
    console.error(`Failed to store snapshot for ${key}:`, e);
    return false;
  }
};

// Clear old snapshots from localStorage
const clearOldSnapshots = () => {
  const keysToRemove: string[] = [];
  const now = new Date().getTime();

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('mapSnapshot_')) {
      const expiryKey = `${key}_expiry`;
      const expiry = localStorage.getItem(expiryKey);

      if (!expiry || now > new Date(expiry).getTime()) {
        keysToRemove.push(key);
        keysToRemove.push(expiryKey);
      }
    }
  }

  // Remove expired items
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // If still not enough space, remove oldest snapshots
  if (keysToRemove.length === 0) {
    const allSnapshots: { key: string; expiry: number }[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('mapSnapshot_') && !key.endsWith('_expiry')) {
        const expiryKey = `${key}_expiry`;
        const expiry = localStorage.getItem(expiryKey);
        if (expiry) {
          allSnapshots.push({
            key,
            expiry: new Date(expiry).getTime(),
          });
        }
      }
    }

    // Sort by expiry date and remove oldest 25%
    allSnapshots.sort((a, b) => a.expiry - b.expiry);
    const toRemove = Math.ceil(allSnapshots.length * 0.25);

    for (let i = 0; i < toRemove; i++) {
      localStorage.removeItem(allSnapshots[i].key);
      localStorage.removeItem(`${allSnapshots[i].key}_expiry`);
    }
  }
};

export const useBaseMapSnapshots = (baseMaps: BaseLayerConfig[]): Snapshots => {
  const [snapshots, setSnapshots] = useState<Snapshots>({});
  const wmsSettings = useWMSSettings();

  const captureMapSnapshot = async (
    config: BaseLayerConfig
  ): Promise<string | null> => {
    const cacheKey = `mapSnapshot_${config.type}_${config.id}_${config.center}_${config.zoom}`;

    try {
      const cachedData = localStorage.getItem(cacheKey);
      const cacheExpiry = localStorage.getItem(`${cacheKey}_expiry`);

      const isCacheValid =
        cachedData &&
        cacheExpiry &&
        new Date().getTime() < new Date(cacheExpiry).getTime();

      if (isCacheValid) {
        return cachedData;
      }
    } catch (e) {
      console.warn('Failed to read from localStorage:', e);
    }

    const mapContainer = document.createElement('div');
    mapContainer.style.position = 'fixed';
    mapContainer.style.top = '0';
    mapContainer.style.left = '0';
    mapContainer.style.width = '120px';
    mapContainer.style.height = '80px';
    mapContainer.style.overflow = 'hidden';
    mapContainer.style.zIndex = '-1';
    document.body.appendChild(mapContainer);

    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '120px';
    overlay.style.height = '80px';
    overlay.style.backgroundColor = '#ffffff';
    overlay.style.zIndex = '1';
    document.body.appendChild(overlay);

    try {
      const map = new Map({
        target: mapContainer,
        view: new View({
          center: config.center || [1401430, 7570938],
          zoom: config.zoom || 8,
        }),
        controls: defaultControls({ zoom: false, attribution: false }),
      });

      let layer;
      if (config.type === 'osm') {
        layer = new OpenLayersTileLayer({
          source: new OSM({
            url: config.url,
            tileLoadFunction: fetchTileWithRetry(config.headers),
          }),
          zIndex: 0,
        });
      } else if (config.type === 's57') {
        layer = S57ToTileLayer(getS57Url());
      } else if (config.type === 'blue') {
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#aad3df';
          ctx.fillRect(0, 0, 256, 256);
        }
        return canvas.toDataURL();
      } else if (config.type === 'wms') {
        for (const wmsInfo of wmsSettings.wmsInfos) {
          const wmsLayerInfo = wmsInfo.layers?.find(
            (layer: WMSLayerInfo) => layer.id === config.id
          );
          if (wmsLayerInfo) {
            layer = wmsToTileLayer(wmsInfo, wmsLayerInfo);
          }
        }
      }

      if (layer) {
        map.addLayer(layer);
      }

      await new Promise((resolve) => {
        map.once('rendercomplete', resolve);
      });

      let dataUrl = await toPng(mapContainer);

      // Compress the image to reduce storage size
      dataUrl = await compressImage(dataUrl, 0.3);

      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7);

      // Try to store with error handling
      const stored = await safeLocalStorageSet(cacheKey, dataUrl);
      if (stored) {
        await safeLocalStorageSet(
          `${cacheKey}_expiry`,
          expiryDate.toISOString()
        );
      }

      return dataUrl;
    } catch (error) {
      console.error('Failed to capture map snapshot:', error);
      return null;
    } finally {
      // Clean up DOM elements
      if (mapContainer.parentNode) {
        document.body.removeChild(mapContainer);
      }
      if (overlay.parentNode) {
        document.body.removeChild(overlay);
      }
    }
  };

  useEffect(() => {
    const generateSnapshots = async () => {
      const snapshotPromises = baseMaps.map((config) =>
        captureMapSnapshot(config)
      );

      try {
        const snapshotsArray = await Promise.all(snapshotPromises);
        const snapshotsObject = snapshotsArray.reduce(
          (acc: Snapshots, snapshot, index) => {
            if (snapshot) {
              acc[baseMaps[index].id] = snapshot;
            }
            return acc;
          },
          {}
        );
        setSnapshots(snapshotsObject);
      } catch (error) {
        console.error('Failed to generate snapshots:', error);
      }
    };

    if (baseMaps.length > 0) {
      generateSnapshots();
    }
  }, [baseMaps, wmsSettings.wmsInfos]);

  return snapshots;
};
